import React, { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '../../locales/i18n';
import { X, Send, Loader2, Mail, CheckCircle, AlertTriangle, FileText } from 'lucide-react';
import { useAuth } from '../auth/AuthContext';
import { clientService } from '../../services/clientService';
import { logger } from '../../utils/logger';
import { formatAmountDisplay } from '../../utils/amountFormatter';
import UploadingModal from '../common/UploadingModal';
import '../confirmations/ExceptionsModal.css';

interface ToleranceSettings {
  currencyTolerances: { currency: string; tolerance: number }[];
  defaultTolerance: number;
}

interface ExpiryResponseModalProps {
  isOpen: boolean;
  trades: any[];                 // all selected trades with expiryState === 'received'
  ourCalculations: any[];
  cptyCalculations: any[];
  clientId: string;
  toleranceSettings?: ToleranceSettings | null;
  onClose: () => void;
  onSuccess: (agreedIds: string[], disputedIds: string[], siStoragePaths: Record<string, string>) => void;
}

interface AccountPreview {
  tradeNumber: string;
  ruleMatched: boolean;
  ruleName?: string;
  cargarAccountId?: string;
  abonarAccountId?: string;
  templateId?: string;
  outputFormat?: string;
}

interface BankAccountInfo {
  id: string;
  accountName: string;
  bankName: string;
  swiftCode: string;
  accountCurrency: string;
  accountNumber: string;
  isDefault: boolean;
  accountType?: 'nostro' | 'vostro';
  counterpartyId?: string;
  counterpartyName?: string;
}

// Fallback tolerances when no admin settings are configured
const FALLBACK_CLP_TOLERANCE = 2;
const FALLBACK_DEFAULT_TOLERANCE = 0.01;

const fmt = (n: number | null | undefined): string => {
  if (n == null) return '—';
  const locale = i18n.language?.substring(0, 2) || 'es';
  return formatAmountDisplay(n, locale, 0);
};

const formatDate = (d: string | undefined): string => {
  if (!d) return '';
  // DD-MM-YYYY → DD/MM/YYYY
  if (/^\d{2}-\d{2}-\d{4}$/.test(d)) return d.replace(/-/g, '/');
  // YYYY-MM-DD → DD/MM/YYYY
  if (/^\d{4}-\d{2}-\d{2}$/.test(d)) {
    const [y, m, day] = d.split('-');
    return `${day}/${m}/${y}`;
  }
  // DD/MM/YYYY already fine
  return d;
};

const getBankRef = (trade: any): string =>
  trade.CounterpartySettlement?.CounterpartyTradeNumber || '';

/** Determine which account dropdowns a trade needs based on settlement type */
const getRequiredAccounts = (trade: any, ourCalc: any) => {
  const isNDF = trade.SettlementType === 'Compensación';
  if (isNDF) {
    // NDF: single net cashflow — only need the relevant side
    return (ourCalc?.WePayAmount || 0) > 0
      ? { needsCargar: true, needsAbonar: false, cargarCurrency: ourCalc?.WePayCurrency }
      : { needsCargar: false, needsAbonar: true, abonarCurrency: ourCalc?.WeReceiveCurrency };
  }
  // Physical delivery: two cashflows
  return {
    needsCargar: true,
    needsAbonar: true,
    cargarCurrency: ourCalc?.WePayCurrency,
    abonarCurrency: ourCalc?.WeReceiveCurrency,
  };
};

const getTolerance = (currency: string, settings?: ToleranceSettings | null): number => {
  if (settings) {
    const entry = settings.currencyTolerances.find(e => e.currency === currency);
    if (entry) return entry.tolerance;
    return settings.defaultTolerance;
  }
  // Fallback when no settings loaded
  return currency === 'CLP' ? FALLBACK_CLP_TOLERANCE : FALLBACK_DEFAULT_TOLERANCE;
};

const ExpiryResponseModal: React.FC<ExpiryResponseModalProps> = ({
  isOpen,
  trades,
  ourCalculations,
  cptyCalculations,
  clientId,
  toleranceSettings,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [netPayments, setNetPayments] = useState(false);
  const [emailOpened, setEmailOpened] = useState(false);
  const [siStoragePaths, setSiStoragePaths] = useState<Record<string, string>>({}); // tradeId → storagePath
  const [siGenerating, setSiGenerating] = useState(false);
  const [siDone, setSiDone] = useState(false);

  // Accepted counterparty values (disputed trades the user has manually accepted)
  const [acceptedTradeIds, setAcceptedTradeIds] = useState<Set<string>>(new Set());

  // Bank account state
  const [bankAccounts, setBankAccounts] = useState<BankAccountInfo[]>([]);
  const [accountPreviews, setAccountPreviews] = useState<Record<string, AccountPreview>>({});
  const [accountSelections, setAccountSelections] = useState<Record<string, { cargarAccountId?: string; abonarAccountId?: string }>>({});
  const [previewLoading, setPreviewLoading] = useState(false);

  const organizationName = user?.organization?.name || 'Company';
  const prefix = 'forwardMaturity.expiryResponse';

  // Classify each trade as agreed or disputed
  // Also track "within tolerance" — amounts differ but within acceptable range
  const { agreed, disputed, discrepancies, withinToleranceFields } = useMemo(() => {
    const agreedList: any[] = [];
    const disputedList: any[] = [];
    const discMap: Record<string, { field: string; ours: string; theirs: string }[]> = {};
    // Track which fields on agreed trades had non-zero differences within tolerance
    const tolFields: Record<string, ('wePay' | 'weReceive')[]> = {};

    for (const trade of trades) {
      const our = ourCalculations.find(c => c.TradeNumber === trade.TradeNumber);
      const cpty = cptyCalculations.find(c => c.TradeNumber === trade.TradeNumber);
      if (!our || !cpty) { agreedList.push(trade); continue; }

      const diffs: { field: string; ours: string; theirs: string }[] = [];
      const tolDiffs: ('wePay' | 'weReceive')[] = [];

      // Note: Direction (Buy/Sell) is intentionally not compared here.
      // WePayAmount and WeReceiveAmount capture the economic substance of the trade.
      // Banks like Banco Internacional write emails from their own perspective, causing
      // LLM direction extraction to produce the opposite label from the client's view.
      // Showing a direction dispute is misleading when the amounts actually agree.

      const wePayTol = getTolerance(our.WePayCurrency || '', toleranceSettings);
      const wePayDiff = (our.WePayAmount != null && cpty.WePayAmount != null)
        ? Math.abs((our.WePayAmount || 0) - (cpty.WePayAmount || 0))
        : 0;
      if (wePayDiff > wePayTol) {
        diffs.push({
          field: `${t(`${prefix}.wePay`)} (${our.WePayCurrency || ''})`,
          ours: fmt(our.WePayAmount),
          theirs: fmt(cpty.WePayAmount),
        });
      } else if (wePayDiff > 0) {
        tolDiffs.push('wePay');
      }

      const weRecvTol = getTolerance(our.WeReceiveCurrency || '', toleranceSettings);
      const weRecvDiff = (our.WeReceiveAmount != null && cpty.WeReceiveAmount != null)
        ? Math.abs((our.WeReceiveAmount || 0) - (cpty.WeReceiveAmount || 0))
        : 0;
      if (weRecvDiff > weRecvTol) {
        diffs.push({
          field: `${t(`${prefix}.weReceive`)} (${our.WeReceiveCurrency || ''})`,
          ours: fmt(our.WeReceiveAmount),
          theirs: fmt(cpty.WeReceiveAmount),
        });
      } else if (weRecvDiff > 0) {
        tolDiffs.push('weReceive');
      }

      if (diffs.length > 0) {
        disputedList.push(trade);
        discMap[trade.TradeNumber] = diffs;
      } else {
        agreedList.push(trade);
        if (tolDiffs.length > 0) {
          tolFields[trade.TradeNumber] = tolDiffs;
        }
      }
    }

    return { agreed: agreedList, disputed: disputedList, discrepancies: discMap, withinToleranceFields: tolFields };
  }, [trades, ourCalculations, cptyCalculations, toleranceSettings, t, prefix]);

  // Fetch account previews when modal opens with agreed trades
  useEffect(() => {
    if (!isOpen) {
      setBankAccounts([]);
      setAccountPreviews({});
      setAccountSelections({});
      setEmailOpened(false);
      setSiStoragePaths({});
      setSiDone(false);
      setAcceptedTradeIds(new Set());
      return;
    }

    if (trades.length === 0) return;

    const fetchPreviews = async () => {
      setPreviewLoading(true);
      try {
        // Fetch for all trades (not just agreed) so accepted trades have previews ready
        const tradeNumbers = trades.map(tr => tr.TradeNumber);
        const resp = await clientService.previewSettlementAccounts(clientId, tradeNumbers);
        if (resp.success && resp.data) {
          setBankAccounts(resp.data.bankAccounts);

          const previewMap: Record<string, AccountPreview> = {};
          const selections: Record<string, { cargarAccountId?: string; abonarAccountId?: string }> = {};

          for (const p of resp.data.previews) {
            previewMap[p.tradeNumber] = p;
            if (p.ruleMatched) {
              selections[p.tradeNumber] = {
                cargarAccountId: p.cargarAccountId,
                abonarAccountId: p.abonarAccountId,
              };
            }
          }

          setAccountPreviews(previewMap);
          setAccountSelections(selections);
        }
      } catch (err) {
        logger.error('Failed to fetch settlement account previews:', err);
      } finally {
        setPreviewLoading(false);
      }
    };

    fetchPreviews();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Group ALL trades by bank
  const groupedByBank = useMemo(() => {
    const groups: Record<string, { bankName: string; bankEmail: string; trades: any[] }> = {};
    for (const trade of trades) {
      const bankId = trade.CounterpartyId || 'unknown';
      if (!groups[bankId]) {
        groups[bankId] = {
          bankName: trade.CounterpartyName || bankId,
          bankEmail: trade.bankEmail || '',
          trades: [],
        };
      }
      groups[bankId].trades.push(trade);
    }
    return groups;
  }, [trades]);

  const agreedSet = useMemo(() => new Set(agreed.map(t => t.TradeNumber)), [agreed]);
  const disputedSet = useMemo(() => new Set(disputed.map(t => t.TradeNumber)), [disputed]);

  // Effective lists: accepted trades move from disputed → agreed for display & account selection
  const effectiveAgreed = useMemo(() => {
    const acceptedFromDisputed = disputed.filter(tr => acceptedTradeIds.has(tr.TradeNumber));
    return [...agreed, ...acceptedFromDisputed];
  }, [agreed, disputed, acceptedTradeIds]);

  const effectiveDisputed = useMemo(() => {
    return disputed.filter(tr => !acceptedTradeIds.has(tr.TradeNumber));
  }, [disputed, acceptedTradeIds]);

  const effectiveAgreedSet = useMemo(() => new Set(effectiveAgreed.map(t => t.TradeNumber)), [effectiveAgreed]);
  const effectiveDisputedSet = useMemo(() => new Set(effectiveDisputed.map(t => t.TradeNumber)), [effectiveDisputed]);

  // Validate all required accounts are selected
  const allAccountsSelected = useMemo(() => {
    if (previewLoading) return false;
    if (effectiveAgreed.length === 0) return true; // Only disputed — no accounts needed
    for (const trade of effectiveAgreed) {
      // For accepted or within-tolerance trades, use counterparty's amounts to determine required accounts
      const useCptyAmounts = acceptedTradeIds.has(trade.TradeNumber) || !!withinToleranceFields[trade.TradeNumber];
      const calc = useCptyAmounts
        ? cptyCalculations.find(c => c.TradeNumber === trade.TradeNumber)
        : ourCalculations.find(c => c.TradeNumber === trade.TradeNumber);
      const req = getRequiredAccounts(trade, calc);
      const sel = accountSelections[trade.TradeNumber];
      if (req.needsCargar && !sel?.cargarAccountId) return false;
      if (req.needsAbonar && !sel?.abonarAccountId) return false;
    }
    return true;
  }, [effectiveAgreed, ourCalculations, cptyCalculations, accountSelections, previewLoading, acceptedTradeIds, withinToleranceFields]);

  /** Render an account dropdown for a specific trade + side */
  const renderAccountDropdown = (
    tradeNumber: string,
    side: 'cargar' | 'abonar',
    currency: string | undefined,
    showLabel: boolean,
    counterpartyId?: string,
  ) => {
    // Cargar (we pay): show nostro accounts + vostro accounts for this counterparty
    // Abonar (we receive): show only nostro accounts
    const filtered = bankAccounts.filter(a => {
      if (a.accountCurrency !== currency) return false;
      const type = a.accountType || 'nostro';
      if (side === 'abonar') return type === 'nostro';
      // cargar: nostro OR vostro belonging to this counterparty
      if (type === 'nostro') return true;
      return type === 'vostro' && a.counterpartyId === counterpartyId;
    });
    const key = side === 'cargar' ? 'cargarAccountId' : 'abonarAccountId';
    const currentValue = accountSelections[tradeNumber]?.[key] || '';
    const label = side === 'cargar' ? t(`${prefix}.cargarAccount`) : t(`${prefix}.abonarAccount`);

    return (
      <div key={`${tradeNumber}-${side}`} style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: showLabel ? '3px' : 0 }}>
        {showLabel && (
          <span style={{ fontSize: '10px', color: 'var(--text-secondary)', fontStyle: 'italic', whiteSpace: 'nowrap' }}>{label}</span>
        )}
        <select
          value={currentValue}
          onChange={(e) => {
            setAccountSelections(prev => ({
              ...prev,
              [tradeNumber]: { ...prev[tradeNumber], [key]: e.target.value || undefined },
            }));
          }}
          style={{
            flex: 1,
            padding: '3px 6px',
            fontSize: '12px',
            borderRadius: '4px',
            border: '1px solid var(--border-color)',
            backgroundColor: 'var(--bg-primary)',
            color: 'var(--text-primary)',
          }}
        >
          <option value="">{t(`${prefix}.selectAccount`)}</option>
          {filtered.length === 0 ? (
            <option disabled>{t(`${prefix}.noAccountsForCurrency`, { currency })}</option>
          ) : (
            filtered.map(acc => {
              const typeLabel = (acc.accountType || 'nostro') === 'vostro' ? ' [Vostro]' : '';
              return (
                <option key={acc.id} value={acc.id}>
                  {acc.bankName} - {acc.accountNumber} ({acc.accountCurrency}){typeLabel}
                </option>
              );
            })
          )}
        </select>
      </div>
    );
  };

  // ── 1. Open Email (repeatable, modal stays open) ──
  const handleOpenEmail = () => {
    for (const [, group] of Object.entries(groupedByBank)) {
      const bankAgreed = group.trades.filter(tr => effectiveAgreedSet.has(tr.TradeNumber));
      const bankDisputed = group.trades.filter(tr => effectiveDisputedSet.has(tr.TradeNumber));

      const maturityDate = formatDate(trades[0]?.MaturityDate);
      const lines: string[] = [];

      // Greeting + intro
      lines.push(t(`${prefix}.emailGreeting`));
      lines.push('');
      lines.push(t(`${prefix}.emailIntro`, { date: maturityDate }));
      lines.push('');

      // ── Agreed section — group by Pagamos then Recibimos ──
      if (bankAgreed.length > 0) {
        lines.push(t(`${prefix}.emailAgreedHeader`));
        lines.push('');

        // Collect all payment items (Pagamos) and receipt items (Recibimos)
        interface CashflowItem {
          bankRef: string;
          tradeNumber: string;
          currency: string;
          amount: number;
          accountId: string;
          accountNumber: string;
          accountBankName: string;
        }
        const pagamos: CashflowItem[] = [];
        const recibimos: CashflowItem[] = [];

        for (const tr of bankAgreed) {
          // For accepted or within-tolerance trades, use counterparty's amounts
          const useCptyAmounts = acceptedTradeIds.has(tr.TradeNumber) || !!withinToleranceFields[tr.TradeNumber];
          const calc = useCptyAmounts
            ? cptyCalculations.find(c => c.TradeNumber === tr.TradeNumber)
            : ourCalculations.find(c => c.TradeNumber === tr.TradeNumber);
          const sel = accountSelections[tr.TradeNumber];
          const bankRef = getBankRef(tr);

          // We Pay leg (cargar)
          if (calc?.WePayAmount && calc.WePayAmount > 0 && sel?.cargarAccountId) {
            const acc = bankAccounts.find(a => a.id === sel.cargarAccountId);
            pagamos.push({
              bankRef,
              tradeNumber: tr.TradeNumber,
              currency: calc.WePayCurrency || '',
              amount: calc.WePayAmount,
              accountId: sel.cargarAccountId,
              accountNumber: acc?.accountNumber || '',
              accountBankName: acc?.bankName || '',
            });
          }

          // We Receive leg (abonar)
          if (calc?.WeReceiveAmount && calc.WeReceiveAmount > 0 && sel?.abonarAccountId) {
            const acc = bankAccounts.find(a => a.id === sel.abonarAccountId);
            recibimos.push({
              bankRef,
              tradeNumber: tr.TradeNumber,
              currency: calc.WeReceiveCurrency || '',
              amount: calc.WeReceiveAmount,
              accountId: sel.abonarAccountId,
              accountNumber: acc?.accountNumber || '',
              accountBankName: acc?.bankName || '',
            });
          }
        }

        // Same-bank: counterparty can debit our account directly → "por favor cargar cuenta"
        // Different-bank: we will transfer → "realizaremos una transferencia ... a cuenta"
        const isSameBank = (accountBankName: string) => accountBankName === group.bankName;

        if (netPayments) {
          // ── Netted format: group by currency+account, compute net ──
          const ledger: Record<string, {
            currency: string; accountId: string; accountNumber: string; accountBankName: string;
            payTotal: number; recvTotal: number;
            payTrades: { bankRef: string; tradeNumber: string; amount: number }[];
            recvTrades: { bankRef: string; tradeNumber: string; amount: number }[];
          }> = {};

          const getKey = (currency: string, accountId: string) => `${currency}::${accountId}`;

          for (const item of pagamos) {
            const key = getKey(item.currency, item.accountId);
            if (!ledger[key]) {
              ledger[key] = {
                currency: item.currency, accountId: item.accountId,
                accountNumber: item.accountNumber, accountBankName: item.accountBankName,
                payTotal: 0, recvTotal: 0, payTrades: [], recvTrades: [],
              };
            }
            ledger[key].payTotal += item.amount;
            ledger[key].payTrades.push({ bankRef: item.bankRef, tradeNumber: item.tradeNumber, amount: item.amount });
          }

          for (const item of recibimos) {
            const key = getKey(item.currency, item.accountId);
            if (!ledger[key]) {
              ledger[key] = {
                currency: item.currency, accountId: item.accountId,
                accountNumber: item.accountNumber, accountBankName: item.accountBankName,
                payTotal: 0, recvTotal: 0, payTrades: [], recvTrades: [],
              };
            }
            ledger[key].recvTotal += item.amount;
            ledger[key].recvTrades.push({ bankRef: item.bankRef, tradeNumber: item.tradeNumber, amount: item.amount });
          }

          for (const entry of Object.values(ledger)) {
            const net = entry.recvTotal - entry.payTotal;
            const allTrades = [...entry.payTrades, ...entry.recvTrades];
            const sameBank = isSameBank(entry.accountBankName);

            if (net > 0) {
              lines.push(`Neto a recibir: ${entry.currency} ${fmt(net)} - por favor abonar cuenta ${entry.accountNumber}, ${entry.accountBankName}.`);
            } else if (net < 0) {
              const amt = `${entry.currency} ${fmt(Math.abs(net))}`;
              if (sameBank) {
                lines.push(`Neto a pagar: ${amt} - por favor cargar cuenta ${entry.accountNumber}.`);
              } else {
                lines.push(`Neto a pagar: ${amt} - realizaremos una transferencia de ${amt} a cuenta ${entry.accountNumber}, ${entry.accountBankName}.`);
              }
            } else {
              lines.push(`Neto: ${entry.currency} 0 (sin movimiento) - cuenta ${entry.accountNumber}, ${entry.accountBankName}.`);
            }

            // Trade breakdown
            for (const tr of allTrades) {
              const isPay = entry.payTrades.some(p => p.tradeNumber === tr.tradeNumber);
              const direction = isPay ? 'Pagamos' : 'Recibimos';
              lines.push(`  - Operación ${tr.bankRef || tr.tradeNumber} (Nuestra ref. ${tr.tradeNumber}): ${direction} ${entry.currency} ${fmt(tr.amount)}`);
            }
            lines.push('');
          }
        } else {
          // ── Non-netted format: Pagamos first, then Recibimos ──
          if (pagamos.length > 0) {
            lines.push('Pagamos:');
            for (const item of pagamos) {
              const ref = item.bankRef
                ? `${item.bankRef} (Nuestra ref. ${item.tradeNumber})`
                : item.tradeNumber;
              const amt = `${item.currency} ${fmt(item.amount)}`;
              if (isSameBank(item.accountBankName)) {
                lines.push(`- Operación número ${ref} - por favor cargar cuenta ${item.accountNumber}, ${amt}.`);
              } else {
                lines.push(`- Operación número ${ref} - realizaremos una transferencia de ${amt} a cuenta ${item.accountNumber}, ${item.accountBankName}.`);
              }
            }
            lines.push('');
          }

          if (recibimos.length > 0) {
            lines.push('Recibimos:');
            for (const item of recibimos) {
              const ref = item.bankRef
                ? `${item.bankRef} (Nuestra ref. ${item.tradeNumber})`
                : item.tradeNumber;
              lines.push(`- Operación número ${ref} - por favor abonar cuenta ${item.accountNumber}, ${item.accountBankName}, ${item.currency} ${fmt(item.amount)}.`);
            }
            lines.push('');
          }
        }
      }

      // Disputed section
      if (bankDisputed.length > 0) {
        lines.push(t(`${prefix}.emailDisputedHeader`));
        const calcUs = t(`${prefix}.calcLabel`, { name: organizationName });
        const calcThem = t(`${prefix}.calcLabel`, { name: group.bankName });
        for (const tr of bankDisputed) {
          const diffs = discrepancies[tr.TradeNumber] || [];
          const bankRef = getBankRef(tr);
          const refStr = bankRef
            ? `${bankRef} (${t(`${prefix}.ourRef`)}: ${tr.TradeNumber})`
            : tr.TradeNumber;
          lines.push(`- ${refStr}:`);
          for (const d of diffs) {
            lines.push(`    ${d.field}: ${calcUs}: ${d.ours} / ${calcThem}: ${d.theirs}`);
          }
        }
        lines.push('');
      }

      // SI attachment note
      const hasSi = siDone && effectiveAgreed.some(tr => accountPreviews[tr.TradeNumber]?.templateId);
      const isMixed = bankAgreed.length > 0 && bankDisputed.length > 0;

      if (hasSi) {
        lines.push(t(`${prefix}.${isMixed ? 'emailSiAttachedMixed' : 'emailSiAttached'}`));
        lines.push('');
      }

      if (bankDisputed.length > 0) {
        lines.push(t(`${prefix}.${isMixed ? 'emailPleaseReviewMixed' : 'emailPleaseReview'}`));
      }

      if (hasSi || bankDisputed.length > 0) {
        lines.push('');
      }

      // Closing
      lines.push(t(`${prefix}.emailClosing`, { organizationName }));

      const body = lines.join('\n');
      const subject = t(`${prefix}.emailSubject`, { date: maturityDate, organizationName });

      const ccValue = trades[0]?.expiryCc || '';
      const ccParam = ccValue ? `&cc=${encodeURIComponent(ccValue)}` : '';
      const mailto = `mailto:${group.bankEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}${ccParam}`;
      window.open(mailto, '_blank');
    }

    setEmailOpened(true);
  };

  // ── 2. Generate Settlement Instructions (separate action) ──
  const handleGenerateSI = async () => {
    setSiGenerating(true);
    try {
      const paths: Record<string, string> = {};
      for (const trade of effectiveAgreed) {
        const preview = accountPreviews[trade.TradeNumber];
        const sel = accountSelections[trade.TradeNumber];

        if (preview?.ruleMatched && preview?.templateId) {
          try {
            const result = await clientService.generateSettlementInstruction(
              clientId,
              trade.TradeNumber,
              trade.CounterpartySettlement?.CounterpartyTradeNumber,
              undefined,
              sel?.cargarAccountId,
              sel?.abonarAccountId,
              'ON_EXPIRY',
            );

            if (result.success && result.data?.storage_path) {
              paths[trade.id] = result.data.storage_path;

              try {
                const urlResp = await clientService.getSettlementInstructionUrl(
                  clientId, undefined, result.data.storage_path
                );
                if (urlResp.success && urlResp.data?.signed_url) {
                  const a = document.createElement('a');
                  a.href = urlResp.data.signed_url;
                  a.download = '';
                  a.style.display = 'none';
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                }
              } catch (dlErr) {
                logger.error(`Failed to download SI for trade ${trade.TradeNumber}:`, dlErr);
              }
            }
          } catch (err) {
            logger.error(`Failed to generate SI for trade ${trade.TradeNumber}:`, err);
          }
        }
      }
      setSiStoragePaths(prev => ({ ...prev, ...paths }));
      setSiDone(true);
    } finally {
      setSiGenerating(false);
    }
  };

  // ── 3. Confirm & Close (commits state, closes modal) ──
  const handleConfirmAndClose = async () => {
    setLoading(true);
    try {
      // Split into 4 groups: exact agreed, within-tolerance agreed, user-accepted (from disputed), remaining disputed
      const exactAgreedIds = agreed.filter(tr => !withinToleranceFields[tr.TradeNumber]).map(tr => tr.id);
      const toleranceAgreedIds = agreed.filter(tr => !!withinToleranceFields[tr.TradeNumber]).map(tr => tr.id);
      const acceptedIds = disputed.filter(tr => acceptedTradeIds.has(tr.TradeNumber)).map(tr => tr.id);
      const remainingDisputedIds = effectiveDisputed.map(tr => tr.id);

      // All IDs that need counterparty values accepted (within-tolerance + user-accepted)
      const allAcceptIds = [...toleranceAgreedIds, ...acceptedIds];

      if (exactAgreedIds.length > 0) {
        await clientService.updateExpiryState(clientId, exactAgreedIds, 'agreed');
      }
      if (allAcceptIds.length > 0) {
        // Accept counterparty values: archives our originals, overwrites with counterparty amounts
        await clientService.acceptCounterpartyValues(clientId, allAcceptIds);
      }
      if (remainingDisputedIds.length > 0) {
        await clientService.updateExpiryState(clientId, remainingDisputedIds, 'disputed');
      }

      onSuccess([...exactAgreedIds, ...allAcceptIds], remainingDisputedIds, siStoragePaths);
    } catch (err) {
      logger.error('Expiry confirm flow failed:', err);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
    <UploadingModal isOpen={loading} message={t(`${prefix}.generating`)} />
    <div className="exceptions-modal-overlay" onClick={onClose}>
      <div className="exceptions-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '950px' }}>

        {/* Header */}
        <div className="exceptions-modal-header">
          <div className="header-title-area">
            <Send size={22} color="var(--accent-blue)" />
            <h2>{t(`${prefix}.title`)}</h2>
          </div>
          <button className="close-btn" onClick={onClose}><X size={20} /></button>
        </div>

        {/* Body */}
        <div className="exceptions-content" style={{ minHeight: 'auto', padding: '20px 24px' }}>

          {Object.entries(groupedByBank).map(([bankId, group]) => (
            <div key={bankId} style={{ marginBottom: '1.5rem' }}>
              {/* Bank header */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
                <Mail size={14} color="var(--text-secondary)" />
                <strong style={{ color: 'var(--text-primary)' }}>{group.bankName}</strong>
                {group.bankEmail ? (
                  <span style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>({group.bankEmail})</span>
                ) : (
                  <span style={{ color: '#dc3545', fontSize: '12px' }}>
                    ({t(`${prefix}.noBankEmail`, { counterparty: group.bankName })})
                  </span>
                )}
              </div>

              {/* Agreed trades — with account dropdowns */}
              {group.trades.some(tr => effectiveAgreedSet.has(tr.TradeNumber)) && (
                <div style={{ marginBottom: '1rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.4rem' }}>
                    <CheckCircle size={14} color="#28a745" />
                    <span style={{ color: '#28a745', fontSize: '13px', fontWeight: 600 }}>
                      {t(`${prefix}.agreedSection`)}
                    </span>
                  </div>

                  {/* Bulk account selector — apply one account to all trades in this bank group */}
                  {(() => {
                    const agreedTrades = group.trades.filter(tr => effectiveAgreedSet.has(tr.TradeNumber));
                    // Collect unique side+currency pairs needed across agreed trades
                    const slots: { side: 'cargar' | 'abonar'; currency: string; counterpartyId?: string }[] = [];
                    const seen = new Set<string>();
                    agreedTrades.forEach(trade => {
                      const isAccepted = acceptedTradeIds.has(trade.TradeNumber);
                      const tolFields = withinToleranceFields[trade.TradeNumber];
                      const useCptyAmounts = isAccepted || !!tolFields;
                      const calc = useCptyAmounts
                        ? cptyCalculations.find(c => c.TradeNumber === trade.TradeNumber)
                        : ourCalculations.find(c => c.TradeNumber === trade.TradeNumber);
                      const req = getRequiredAccounts(trade, calc);
                      if (req.needsCargar && req.cargarCurrency) {
                        const k = `cargar-${req.cargarCurrency}`;
                        if (!seen.has(k)) { seen.add(k); slots.push({ side: 'cargar', currency: req.cargarCurrency, counterpartyId: trade.CounterpartyId }); }
                      }
                      if (req.needsAbonar && req.abonarCurrency) {
                        const k = `abonar-${req.abonarCurrency}`;
                        if (!seen.has(k)) { seen.add(k); slots.push({ side: 'abonar', currency: req.abonarCurrency }); }
                      }
                    });

                    if (slots.length === 0) return null;

                    return (
                      <div style={{
                        display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center',
                        justifyContent: 'flex-end',
                        padding: '6px 8px', marginBottom: '6px',
                        backgroundColor: 'rgba(100, 149, 237, 0.06)',
                        borderRadius: '4px', border: '1px solid rgba(100, 149, 237, 0.15)',
                        fontSize: '12px',
                      }}>
                        <span style={{ color: 'var(--text-secondary)', fontWeight: 500, whiteSpace: 'nowrap' }}>
                          {t(`${prefix}.applyToAll`)}:
                        </span>
                        {slots.map(slot => {
                          const filtered = bankAccounts.filter(a => {
                            if (a.accountCurrency !== slot.currency) return false;
                            const type = a.accountType || 'nostro';
                            if (slot.side === 'abonar') return type === 'nostro';
                            if (type === 'nostro') return true;
                            return type === 'vostro' && a.counterpartyId === slot.counterpartyId;
                          });
                          const label = slot.side === 'cargar'
                            ? `${t(`${prefix}.cargarAccount`)} (${slot.currency})`
                            : `${t(`${prefix}.abonarAccount`)} (${slot.currency})`;
                          const key = slot.side === 'cargar' ? 'cargarAccountId' : 'abonarAccountId';

                          return (
                            <div key={`bulk-${slot.side}-${slot.currency}`} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <span style={{ color: 'var(--text-secondary)', fontStyle: 'italic', whiteSpace: 'nowrap' }}>{label}</span>
                              <select
                                value=""
                                onChange={(e) => {
                                  if (!e.target.value) return;
                                  const accountId = e.target.value;
                                  setAccountSelections(prev => {
                                    const next = { ...prev };
                                    agreedTrades.forEach(trade => {
                                      const isAccepted = acceptedTradeIds.has(trade.TradeNumber);
                                      const tolFields = withinToleranceFields[trade.TradeNumber];
                                      const useCpty = isAccepted || !!tolFields;
                                      const calc = useCpty
                                        ? cptyCalculations.find(c => c.TradeNumber === trade.TradeNumber)
                                        : ourCalculations.find(c => c.TradeNumber === trade.TradeNumber);
                                      const req = getRequiredAccounts(trade, calc);
                                      const matchesSide = slot.side === 'cargar' ? req.needsCargar && req.cargarCurrency === slot.currency
                                        : req.needsAbonar && req.abonarCurrency === slot.currency;
                                      if (matchesSide) {
                                        next[trade.TradeNumber] = { ...next[trade.TradeNumber], [key]: accountId };
                                      }
                                    });
                                    return next;
                                  });
                                }}
                                style={{
                                  padding: '2px 4px', fontSize: '11px', borderRadius: '3px',
                                  border: '1px solid var(--border-color)',
                                  backgroundColor: 'var(--bg-primary)', color: 'var(--text-primary)',
                                  maxWidth: '220px',
                                }}
                              >
                                <option value="">{t(`${prefix}.selectAccount`)}</option>
                                {filtered.map(acc => {
                                  const typeLabel = (acc.accountType || 'nostro') === 'vostro' ? ' [Vostro]' : '';
                                  return (
                                    <option key={acc.id} value={acc.id}>
                                      {acc.bankName} - {acc.accountNumber} ({acc.accountCurrency}){typeLabel}
                                    </option>
                                  );
                                })}
                              </select>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}

                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid var(--border-color)' }}>
                        <th style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--text-secondary)', fontWeight: 500 }}>
                          {t(`${prefix}.bankRef`)}
                        </th>
                        <th style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--text-secondary)', fontWeight: 500 }}>
                          {t(`${prefix}.ourRef`)}
                        </th>
                        <th style={{ textAlign: 'right', padding: '6px 8px', color: 'var(--text-secondary)', fontWeight: 500 }}>
                          {t(`${prefix}.wePay`)}
                        </th>
                        <th style={{ textAlign: 'right', padding: '6px 8px', color: 'var(--text-secondary)', fontWeight: 500 }}>
                          {t(`${prefix}.weReceive`)}
                        </th>
                        <th style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--text-secondary)', fontWeight: 500, minWidth: '180px' }}>
                          {t(`${prefix}.bankAccounts`)}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {group.trades.filter(tr => effectiveAgreedSet.has(tr.TradeNumber)).map((trade) => {
                        const isAccepted = acceptedTradeIds.has(trade.TradeNumber);
                        const tolFields = withinToleranceFields[trade.TradeNumber];
                        const useCptyAmounts = isAccepted || !!tolFields;
                        // For accepted or within-tolerance trades, use counterparty's amounts
                        const calc = useCptyAmounts
                          ? cptyCalculations.find(c => c.TradeNumber === trade.TradeNumber)
                          : ourCalculations.find(c => c.TradeNumber === trade.TradeNumber);
                        const req = getRequiredAccounts(trade, calc);
                        return (
                          <tr key={trade.id} style={{ borderBottom: '1px solid var(--border-color)', backgroundColor: isAccepted ? 'rgba(40, 167, 69, 0.05)' : undefined }}>
                            <td style={{ padding: '6px 8px', color: 'var(--text-primary)', fontWeight: 500 }}>
                              {getBankRef(trade)}
                              {isAccepted && (
                                <span style={{
                                  marginLeft: '6px',
                                  fontSize: '10px',
                                  fontWeight: 600,
                                  color: '#28a745',
                                  backgroundColor: 'rgba(40, 167, 69, 0.1)',
                                  padding: '1px 5px',
                                  borderRadius: '3px',
                                  verticalAlign: 'middle',
                                }}>
                                  {t(`${prefix}.accepted`, 'Accepted')}
                                </span>
                              )}
                            </td>
                            <td style={{ padding: '6px 8px', color: 'var(--text-secondary)' }}>
                              {trade.TradeNumber}
                              {isAccepted && (
                                <button
                                  onClick={() => setAcceptedTradeIds(prev => { const next = new Set(prev); next.delete(trade.TradeNumber); return next; })}
                                  style={{ marginLeft: '6px', background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '11px', textDecoration: 'underline', padding: 0 }}
                                >
                                  {t(`${prefix}.undoAccept`, 'Undo')}
                                </button>
                              )}
                            </td>
                            <td style={{ padding: '6px 8px', textAlign: 'right', color: tolFields?.includes('wePay') ? '#e67e00' : 'var(--text-primary)', fontWeight: tolFields?.includes('wePay') ? 600 : undefined }}>
                              {calc?.WePayAmount ? `${calc.WePayCurrency} ${fmt(calc.WePayAmount)}` : '—'}
                            </td>
                            <td style={{ padding: '6px 8px', textAlign: 'right', color: tolFields?.includes('weReceive') ? '#e67e00' : 'var(--text-primary)', fontWeight: tolFields?.includes('weReceive') ? 600 : undefined }}>
                              {calc?.WeReceiveAmount ? `${calc.WeReceiveCurrency} ${fmt(calc.WeReceiveAmount)}` : '—'}
                            </td>
                            <td style={{ padding: '6px 8px' }}>
                              {previewLoading ? (
                                <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                                  <Loader2 size={12} className="spin" style={{ display: 'inline', marginRight: '4px' }} />
                                  {t(`${prefix}.loadingAccounts`)}
                                </span>
                              ) : (
                                <>
                                  {req.needsCargar && renderAccountDropdown(
                                    trade.TradeNumber, 'cargar', req.cargarCurrency,
                                    req.needsCargar && req.needsAbonar,
                                    trade.CounterpartyId,
                                  )}
                                  {req.needsAbonar && renderAccountDropdown(
                                    trade.TradeNumber, 'abonar', req.abonarCurrency,
                                    req.needsCargar && req.needsAbonar,
                                  )}
                                </>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      {(() => {
                        const agreedTrades = group.trades.filter(tr => effectiveAgreedSet.has(tr.TradeNumber));
                        const wePayTotals: { [ccy: string]: number } = {};
                        const weReceiveTotals: { [ccy: string]: number } = {};

                        agreedTrades.forEach(trade => {
                          const isAccepted = acceptedTradeIds.has(trade.TradeNumber);
                          const tolFields = withinToleranceFields[trade.TradeNumber];
                          const useCptyAmounts = isAccepted || !!tolFields;
                          const calc = useCptyAmounts
                            ? cptyCalculations.find(c => c.TradeNumber === trade.TradeNumber)
                            : ourCalculations.find(c => c.TradeNumber === trade.TradeNumber);
                          if (calc?.WePayAmount && calc.WePayCurrency) {
                            wePayTotals[calc.WePayCurrency] = (wePayTotals[calc.WePayCurrency] || 0) + calc.WePayAmount;
                          }
                          if (calc?.WeReceiveAmount && calc.WeReceiveCurrency) {
                            weReceiveTotals[calc.WeReceiveCurrency] = (weReceiveTotals[calc.WeReceiveCurrency] || 0) + calc.WeReceiveAmount;
                          }
                        });

                        const allCurrencies = Array.from(new Set([...Object.keys(wePayTotals), ...Object.keys(weReceiveTotals)])).sort();
                        if (allCurrencies.length === 0) return null;

                        return allCurrencies.flatMap((ccy, i) => {
                          const payTotal = wePayTotals[ccy] || 0;
                          const recvTotal = weReceiveTotals[ccy] || 0;
                          const netAmount = recvTotal - payTotal;

                          return [
                            <tr key={`total-${ccy}`} style={{ borderTop: i === 0 ? '2px solid var(--border-color)' : '1px solid var(--border-color)', fontWeight: 'bold' }}>
                              <td colSpan={2} style={{ padding: '6px 8px', textAlign: 'right' }}>
                                {i === 0 ? 'Totals:' : ''}
                              </td>
                              <td style={{ padding: '6px 8px', textAlign: 'right' }}>
                                {payTotal > 0 ? `${ccy} ${fmt(payTotal)}` : '—'}
                              </td>
                              <td style={{ padding: '6px 8px', textAlign: 'right' }}>
                                {recvTotal > 0 ? `${ccy} ${fmt(recvTotal)}` : '—'}
                              </td>
                              <td></td>
                            </tr>,
                            <tr key={`net-${ccy}`} style={{ fontWeight: 'bold', fontStyle: 'italic' }}>
                              <td colSpan={2} style={{ padding: '4px 8px', textAlign: 'right', color: 'var(--text-secondary)', fontSize: '12px' }}>
                                {t('forwardMaturity.net')}:
                              </td>
                              <td style={{ padding: '4px 8px', textAlign: 'right', color: netAmount < 0 ? '#dc3545' : 'transparent' }}>
                                {netAmount < 0 ? `${ccy} ${fmt(Math.abs(netAmount))}` : ''}
                              </td>
                              <td style={{ padding: '4px 8px', textAlign: 'right', color: netAmount >= 0 ? '#28a745' : 'transparent' }}>
                                {netAmount >= 0 ? `${ccy} ${fmt(netAmount)}` : ''}
                              </td>
                              <td></td>
                            </tr>,
                          ];
                        });
                      })()}
                    </tfoot>
                  </table>
                </div>
              )}

              {/* Disputed trades — bank ref first, entity-named calc columns */}
              {group.trades.some(tr => effectiveDisputedSet.has(tr.TradeNumber)) && (
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.4rem' }}>
                    <AlertTriangle size={14} color="#ffc107" />
                    <span style={{ color: '#ffc107', fontSize: '13px', fontWeight: 600 }}>
                      {t(`${prefix}.disputedSection`)}
                    </span>
                  </div>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid var(--border-color)' }}>
                        <th style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--text-secondary)', fontWeight: 500 }}>
                          {t(`${prefix}.bankRef`)}
                        </th>
                        <th style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--text-secondary)', fontWeight: 500 }}>
                          {t(`${prefix}.field`)}
                        </th>
                        <th style={{ textAlign: 'right', padding: '6px 8px', color: 'var(--text-secondary)', fontWeight: 500 }}>
                          {t(`${prefix}.calcLabel`, { name: organizationName })}
                        </th>
                        <th style={{ textAlign: 'right', padding: '6px 8px', color: 'var(--text-secondary)', fontWeight: 500 }}>
                          {t(`${prefix}.calcLabel`, { name: group.bankName })}
                        </th>
                        <th style={{ textAlign: 'center', padding: '6px 8px', color: 'var(--text-secondary)', fontWeight: 500, width: '70px' }}>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {group.trades.filter(tr => effectiveDisputedSet.has(tr.TradeNumber)).flatMap((trade) => {
                        const diffs = discrepancies[trade.TradeNumber] || [];
                        const bankRef = getBankRef(trade);
                        return diffs.map((d, i) => (
                          <tr key={`${trade.TradeNumber}-${i}`} style={{ borderBottom: '1px solid var(--border-color)' }}>
                            <td style={{ padding: '6px 8px', color: 'var(--text-primary)', fontWeight: i === 0 ? 500 : 400 }}>
                              {i === 0 ? (
                                <>
                                  {bankRef}
                                  <span style={{ color: 'var(--text-secondary)', fontSize: '11px', marginLeft: '4px' }}>
                                    ({trade.TradeNumber})
                                  </span>
                                </>
                              ) : ''}
                            </td>
                            <td style={{ padding: '6px 8px', color: 'var(--text-primary)' }}>{d.field}</td>
                            <td style={{ padding: '6px 8px', textAlign: 'right', color: 'var(--text-primary)' }}>{d.ours}</td>
                            <td style={{ padding: '6px 8px', textAlign: 'right', color: '#dc3545', fontWeight: 600 }}>{d.theirs}</td>
                            <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                              {i === 0 && (
                                <button
                                  onClick={() => setAcceptedTradeIds(prev => { const next = new Set(prev); next.add(trade.TradeNumber); return next; })}
                                  style={{
                                    padding: '2px 8px',
                                    fontSize: '11px',
                                    fontWeight: 600,
                                    color: '#28a745',
                                    backgroundColor: 'rgba(40, 167, 69, 0.1)',
                                    border: '1px solid rgba(40, 167, 69, 0.3)',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    whiteSpace: 'nowrap',
                                  }}
                                  title={t(`${prefix}.acceptTooltip`, 'Accept counterparty values and move to agreed')}
                                >
                                  {t(`${prefix}.accept`, 'Accept')} ✓
                                </button>
                              )}
                            </td>
                          </tr>
                        ));
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Footer */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '0.75rem',
          padding: '16px 24px',
          borderTop: '1px solid var(--border-color)',
          background: 'var(--bg-secondary)',
          borderRadius: '0 0 12px 12px',
        }}>
          {/* Netting checkbox — left side */}
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '13px', color: 'var(--text-primary)', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={netPayments}
              onChange={(e) => setNetPayments(e.target.checked)}
              disabled={effectiveAgreed.length < 2}
            />
            {t(`${prefix}.netPayments`, 'Netear pago por cuenta')}
          </label>

          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
          <button
            onClick={onClose}
            disabled={loading || siGenerating}
            style={{
              padding: '8px 16px',
              backgroundColor: 'transparent',
              border: '1px solid var(--border-color)',
              color: 'var(--text-secondary)',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
            }}
          >
            {t(`${prefix}.cancel`)}
          </button>

          {/* Abrir Email — repeatable, modal stays open */}
          <button
            onClick={handleOpenEmail}
            disabled={!allAccountsSelected || loading || siGenerating}
            style={{
              padding: '8px 16px',
              backgroundColor: !allAccountsSelected ? 'var(--text-secondary)' : 'var(--accent-blue)',
              color: '#fff',
              border: 'none',
              borderRadius: '6px',
              cursor: !allAccountsSelected || loading ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              fontWeight: 600,
              fontSize: '14px',
              opacity: !allAccountsSelected ? 0.6 : 1,
            }}
          >
            <Mail size={16} />
            {t(`${prefix}.openEmail`)}
          </button>

          {/* Generar Carta Instrucción — only if any agreed trades have SI templates */}
          {effectiveAgreed.some(tr => accountPreviews[tr.TradeNumber]?.templateId) && (
            <button
              onClick={handleGenerateSI}
              disabled={!allAccountsSelected || siGenerating || loading}
              style={{
                padding: '8px 16px',
                backgroundColor: siDone ? '#28a745' : '#6c757d',
                color: '#fff',
                border: 'none',
                borderRadius: '6px',
                cursor: !allAccountsSelected || siGenerating ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                fontWeight: 600,
                fontSize: '14px',
              }}
            >
              {siGenerating ? (
                <>
                  <Loader2 size={16} className="spin" />
                  {t(`${prefix}.generating`)}
                </>
              ) : (
                <>
                  <FileText size={16} />
                  {siDone
                    ? t(`${prefix}.siGenerated`, 'Carta Generada')
                    : t(`${prefix}.generateSI`, 'Generar Carta')
                  }
                </>
              )}
            </button>
          )}

          {/* Confirmar y Cerrar — commits state */}
          <button
            onClick={handleConfirmAndClose}
            disabled={loading || siGenerating}
            style={{
              padding: '8px 16px',
              backgroundColor: '#28a745',
              color: '#fff',
              border: 'none',
              borderRadius: '6px',
              cursor: loading ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              fontWeight: 600,
              fontSize: '14px',
            }}
          >
            {loading ? (
              <>
                <Loader2 size={16} className="spin" />
                {t(`${prefix}.confirming`, 'Confirmando...')}
              </>
            ) : (
              <>
                <CheckCircle size={16} />
                {t(`${prefix}.confirmAndClose`, 'Confirmar y Cerrar')}
              </>
            )}
          </button>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default ExpiryResponseModal;
